import { useId } from 'react';
import { Box, Typography } from '@mui/material';

import {
  DEMO_ACCENT,
  DEMO_FIELD_LABEL,
  DEMO_FIELD_PLACEHOLDER,
  DEMO_FIELD_SHELL,
  DEMO_FIELD_TEXT,
} from './constants';

const shellSx = {
  backgroundColor: DEMO_FIELD_SHELL,
  boxShadow: '0px -1px 0px 0px #3C3F40 inset, 0px 1px 0px 0px #3C3F41 inset',
  p: 2,
  transition: 'box-shadow 120ms ease',
  '&:focus-within': {
    boxShadow: `inset 0 0 0 2px ${DEMO_ACCENT}`,
  },
};

const controlSx = {
  width: '100%',
  background: 'transparent',
  color: DEMO_FIELD_TEXT,
  WebkitTextFillColor: DEMO_FIELD_TEXT,
  caretColor: DEMO_FIELD_TEXT,
  border: 0,
  outline: 'none',
  fontSize: 16,
  fontFamily: 'inherit',
  lineHeight: 1.5,
  p: 0,
  boxSizing: 'border-box',
  '&::placeholder': {
    color: DEMO_FIELD_PLACEHOLDER,
    opacity: 1,
  },
  '&:disabled': {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
};

/**
 * Demo intake control using the app's native dark FormInput chrome.
 */
const DemoField = ({
  label,
  helperText,
  required = false,
  multiline = false,
  minRows = 3,
  placeholder,
  inputRef,
  value = '',
  id: idProp,
  sx,
  ...rest
}) => {
  const generatedId = useId();
  const fieldId = idProp || generatedId;
  const helperId = helperText ? `${fieldId}-helper` : undefined;

  const sharedProps = {
    id: fieldId,
    ref: inputRef,
    value,
    required,
    placeholder,
    'aria-describedby': helperId,
    ...rest,
  };

  return (
    <Box sx={sx}>
      <Box sx={shellSx}>
        {label && (
          <Typography
            component="label"
            htmlFor={fieldId}
            sx={{ color: DEMO_FIELD_LABEL, fontSize: 12, fontWeight: 700, mb: 0.75, display: 'block' }}
          >
            {label}
            {required && (
              <Box component="span" aria-hidden="true" sx={{ color: DEMO_ACCENT, ml: 0.5 }}>
                *
              </Box>
            )}
          </Typography>
        )}

        {multiline ? (
          <Box
            component="textarea"
            {...sharedProps}
            sx={{ ...controlSx, minHeight: minRows * 24, resize: 'vertical' }}
          />
        ) : (
          <Box
            component="input"
            {...sharedProps}
            sx={{ ...controlSx, height: 24, resize: 'none' }}
          />
        )}
      </Box>

      {helperText && (
        <Typography id={helperId} mt={1} fontSize={13} sx={{ opacity: 0.75, lineHeight: 1.45 }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export default DemoField;
